import type { AppDispatch } from 'app/store';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from './bus/ahuBoardBus';
import { roundBasedOnArguments } from './components/toolbar/resolveCatalog';

/**
 * Phase 6e1 — Equalize tunnel lengths (§8.6): the port of
 * `ahu/mvc/view/align_tunnel.js` `alignAHUTunnels`.
 *
 *   if any section has `sitting_on_id`:
 *     while (await alignDualTunnelSections()) {}   // ONE change per pass
 *   alignTunnelLengths()                           // grow every short tunnel
 *                                                  // up to the longest
 *
 * [NEG]#45 honored: the single-target `alignTunnel` variant is not ported.
 *
 * TWO THINGS THIS FILE HAS TO GET RIGHT, both learned the hard way:
 *
 * 1. WHICH SECTION GROWS. Legacy takes `sections[tunnelId].models[0]` — the
 *    collection's first model, and that collection's comparator is `order_rate`
 *    ascending (`section/collection/sections.js:66-68`) with NO airflow
 *    reversal. The reversal belongs to the CANVAS, not to this algorithm; a
 *    port that reversed here grew the LAST section of every right→left tunnel
 *    and landed on a different layout from dev.
 *
 * 2. IT MUST NOT ROUND-TRIP PER STEP. Legacy's `alignTunnelLengths` is a
 *    synchronous loop of local `section.set()` calls — the model's own
 *    `onchange` (`section/model/section.js:288`) fires the PATCH in the
 *    background — so the canvas redraws ONCE, from state that is already
 *    final. The first React port awaited a PATCH *and* a forced section
 *    refetch per tunnel per pass, so the board redrew four to six times during
 *    one click and blanked the canvas between renders. Here the fixed point is
 *    computed locally and the PATCHes go out together; `patchAhuSection`
 *    applies each to the cache optimistically (and re-sums the owning tunnel —
 *    `tunnelLength.ts`), so one batch is one redraw and no refetch is needed.
 */

/** `updateSectionLength` (align_tunnel.js:296-299) — verbatim, these two only. */
const PLENUM_INSERT_TYPES = ['energy_wheel', 'plate_heat_exchanger'];

/**
 * Legacy loops `while (updated)` unbounded. Both loops here converge (lengths
 * only grow, towards a maximum only the increment rounding can raise), but a
 * bound keeps a data anomaly from hanging the tab instead of mis-drawing one
 * unit.
 */
const MAX_PASSES = 25;

/** Float slack: section lengths are increment-rounded, their sums are not. */
const EPS = 0.005;

interface AlignContext {
  tunnels: IAhuBoardTunnel[];
  sectionsByTunnel: Record<number, IAhuBoardSection[]>;
}

/** Collection order: `order_rate` ascending (sections.js comparator). */
const orderedSections = (ctx: AlignContext, tunnelId: number): IAhuBoardSection[] =>
  [...(ctx.sectionsByTunnel[tunnelId] ?? [])].sort(
    (a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0)
  );

const sumLengths = (sections: IAhuBoardSection[]): number =>
  sections.reduce((sum, s) => sum + (Number(s.length) || 0), 0);

const incrementOf = (section: IAhuBoardSection): number =>
  Number((section as { increment?: unknown }).increment) || 0;

/**
 * `getDim` (align_tunnel.js:376-394) — the two halves of a tunnel either side
 * of its dual section, named in AIRFLOW terms: `beforeLength` is upstream of
 * the dual section and `afterLength` downstream, which swap over for a
 * right→left (airflow 2) tunnel.
 */
interface TunnelPart {
  beforeLength: number;
  afterLength: number;
  airflow: number | null | undefined;
  sections: IAhuBoardSection[];
}

const getDim = (
  sections: IAhuBoardSection[],
  airflow: number | null | undefined,
  dualIndex: number
): TunnelPart => {
  const before = sections.slice(0, dualIndex);
  const after = sections.slice(dualIndex + 1);
  return {
    beforeLength: airflow === 1 ? sumLengths(before) : sumLengths(after),
    afterLength: airflow === 1 ? sumLengths(after) : sumLengths(before),
    airflow,
    sections,
  };
};

export async function alignAhuTunnels(
  dispatch: AppDispatch,
  getContext: () => AlignContext
): Promise<void> {
  const patchLength = (section: IAhuBoardSection, newLength: number) =>
    dispatch(
      ahuBoardFormsApi.endpoints.patchAhuSection.initiate({
        id: section.id,
        tunnelId: section.tunnel_id,
        data: { length: newLength, auto_adjust: false },
      })
    ).unwrap();

  /**
   * `updateSectionLength` (align_tunnel.js:295-325). Growing INTO an energy
   * wheel / plate heat exchanger is not allowed — those are sized by their
   * component — so the slack is taken up by a new plenum beside it instead, at
   * order_rate 1 upstream or `section.order_rate + 1` downstream.
   */
  const updateSectionLength = async (
    section: IAhuBoardSection,
    diff: number,
    upstream: boolean
  ): Promise<void> => {
    const increment = incrementOf(section);
    if (PLENUM_INSERT_TYPES.includes(String(section.type ?? ''))) {
      await dispatch(
        ahuBoardFormsApi.endpoints.createAhuSection.initiate({
          tag: '',
          tunnel_id: section.tunnel_id,
          length: roundBasedOnArguments(diff, increment),
          order_rate: upstream ? 1 : (section.order_rate ?? 0) + 1,
          sitting_on_id: null,
          split: false,
        })
      ).unwrap();
      return;
    }
    await patchLength(
      section,
      roundBasedOnArguments((Number(section.length) || 0) + diff, increment)
    );
  };

  /** `adjustTunnelPart` (align_tunnel.js:250-293) — grows ONE section, or none. */
  const adjustTunnelPart = async (
    part: TunnelPart,
    maxBefore: number,
    maxAfter: number
  ): Promise<boolean> => {
    const { sections, airflow } = part;
    const beforeDiff = maxBefore - part.beforeLength;
    const afterDiff = maxAfter - part.afterLength;
    const first = sections[0];
    const last = sections[sections.length - 1];
    const beforeTarget = airflow === 1 ? first : last;
    const afterTarget = airflow === 1 ? last : first;

    if (beforeDiff > EPS && beforeTarget) {
      await updateSectionLength(beforeTarget, beforeDiff, airflow === 1);
      return true;
    }
    if (afterDiff > EPS && afterTarget) {
      await updateSectionLength(afterTarget, afterDiff, airflow === 2);
      return true;
    }
    return false;
  };

  /**
   * `alignDualTunnelSections` (align_tunnel.js:94-147). For every tunnel that
   * holds a dual (stacked) section, line its upstream and downstream halves up
   * with the PREVIOUS tunnel's halves around the section it sits on. Returns
   * after the FIRST change so the caller re-reads everything — legacy does the
   * same, and it is what makes a mid-loop plenum insert safe.
   */
  const alignDualTunnelSections = async (): Promise<boolean> => {
    const ctx = getContext();
    for (let t = 0; t < ctx.tunnels.length; t += 1) {
      const tunnel = ctx.tunnels[t];
      const prevTunnel = ctx.tunnels[t - 1];
      if (!prevTunnel) continue;

      const sections = orderedSections(ctx, tunnel.id);
      const dualIndex = sections.findIndex((s) => s.sitting_on_id);
      if (dualIndex === -1) continue;

      const prevSections = orderedSections(ctx, prevTunnel.id);
      const sittingIndex = prevSections.findIndex(
        (s) => s.id === sections[dualIndex].sitting_on_id
      );
      if (sittingIndex === -1) continue;

      const parts = [
        getDim(sections, tunnel.airflow_direction_id, dualIndex),
        getDim(prevSections, prevTunnel.airflow_direction_id, sittingIndex),
      ];
      const maxBefore = Math.max(...parts.map((p) => p.beforeLength));
      const maxAfter = Math.max(...parts.map((p) => p.afterLength));

      for (const part of parts) {
        // Sequential by design: legacy returns on the first change.
        if (await adjustTunnelPart(part, maxBefore, maxAfter)) return true;
      }
    }
    return false;
  };

  /**
   * `alignTunnelLengths` (align_tunnel.js:327-360). Every tunnel shorter than
   * the longest grows its FIRST section by the difference, repeatedly, because
   * the increment rounding can push a tunnel past the old maximum and make it
   * the new one.
   *
   * Legacy runs this against live Backbone models; here the fixed point is
   * computed on a local copy and only the sections that actually moved are
   * PATCHed — same result, one redraw instead of one per tunnel per pass.
   */
  const alignTunnelLengths = async (): Promise<void> => {
    const ctx = getContext();
    const working = new Map<number, IAhuBoardSection[]>();
    ctx.tunnels.forEach((t) =>
      working.set(
        t.id,
        orderedSections(ctx, t.id).map((s) => ({ ...s }))
      )
    );
    const grown = new Map<number, IAhuBoardSection>();

    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
      const lengths = ctx.tunnels.map((t) => sumLengths(working.get(t.id) ?? []));
      const maxLength = Math.max(...lengths, 0);
      let updated = false;

      ctx.tunnels.forEach((t, i) => {
        const diff = maxLength - lengths[i];
        if (diff <= EPS) return;
        const first = (working.get(t.id) ?? [])[0];
        if (!first) return;
        first.length = roundBasedOnArguments(
          (Number(first.length) || 0) + diff,
          incrementOf(first)
        );
        grown.set(first.id, first);
        updated = true;
      });

      if (!updated) break;
    }

    // One batch: React coalesces the optimistic cache writes into a single
    // render, so the canvas redraws once and already equalized.
    await Promise.all(
      [...grown.values()].map((s) =>
        patchLength(s, Number(s.length) || 0).catch(() => {
          /* one save failed — the rest still land, as in legacy */
        })
      )
    );
  };

  const hasDualSection = Object.values(getContext().sectionsByTunnel)
    .flat()
    .some((s) => s.sitting_on_id);

  if (hasDualSection) {
    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
      // Sequential by design: legacy's `while (updated = await …)` loop.
      if (!(await alignDualTunnelSections())) break;
    }
  }

  await alignTunnelLengths();

  ahuBoardBus.emit('redraw:canvas');
}
